import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Stepper, 
  Step, 
  StepLabel,
  CircularProgress,
  Paper,
  Alert,
  AlertTitle,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Link,
  Grid,
  Card,
  CardContent,
  CardMedia,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import { useMsal } from '@azure/msal-react';
import { InteractionRequiredAuthError, AccountInfo } from '@azure/msal-browser';

// Icons for improved UI
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import SecurityIcon from '@mui/icons-material/Security';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import AppSettingsAltIcon from '@mui/icons-material/AppSettingsAlt';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import InfoIcon from '@mui/icons-material/Info';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ErrorIcon from '@mui/icons-material/Error';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import BuildIcon from '@mui/icons-material/Build';

interface DebugInfo {
  tokenAcquired?: boolean;
  scopes?: string[];
  timestamp?: string;
  tokenExpiration?: string;
  consentWindowClosed?: boolean;
  closedAt?: string;
  apiCallFailed?: boolean;
  apiErrorText?: string;
  error?: string;
  errorCode?: string;
  [key: string]: any; // For any additional properties
}

interface TenantManagerProps {
  onOnboardingComplete: () => void;
  onCancel?: () => void;
  tenantVerified?: boolean; 
  clientId: string;
}

const TenantManager: React.FC<TenantManagerProps> = ({ 
  onOnboardingComplete,
  onCancel = () => instance.logout(), // Default to logout if no handler provided
  tenantVerified = false, // Optional prop to skip onboarding if tenant is already verified
  clientId
}) => {
  const { instance, accounts } = useMsal();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [adminConsentUrl, setAdminConsentUrl] = useState<string | null>(null);
  const [consentCheckInterval, setConsentCheckInterval] = useState<number | null>(null);
  const [showDebugInfo, setShowDebugInfo] = useState(false);
  const [debugInfo, setDebugInfo] = useState<DebugInfo>({});
  const [showPermissionsInfo, setShowPermissionsInfo] = useState(false);
  const [showWelcomeDialog, setShowWelcomeDialog] = useState(true);
  const [consentVerificationState, setConsentVerificationState] = useState<
    'idle' | 'waiting' | 'checking' | 'success' | 'failed'
  >('idle');
  
  // Get current account details
  const currentAccount = accounts[0];
  const tenantId = currentAccount?.tenantId;
  const userEmail = currentAccount?.username || '';
  const userDisplayName = currentAccount?.name || userEmail.split('@')[0];
  
  // Define all required scopes for the app to function
  const allRequiredScopes = [
    "User.Read",
    "Directory.Read.All",
    "Application.ReadWrite.All",
    "Policy.Read.All",
    "Policy.ReadWrite.ApplicationConfiguration"
  ];

  // Map permissions to human-readable descriptions
  const permissionDescriptions: Record<string, string> = {
    "User.Read": "Read your basic profile information",
    "Directory.Read.All": "Read directory data to discover applications and users",
    "Application.ReadWrite.All": "Manage application registrations and settings",
    "Policy.Read.All": "Read authentication policy configurations",
    "Policy.ReadWrite.ApplicationConfiguration": "Manage claims mapping policies for applications"
  };

  // Extract parameters from a URL
  const extractParamsFromUrl = (url: string) => {
    try {
      const urlObj = new URL(url);
      const params = new URLSearchParams(urlObj.search);
      
      return {
        adminConsent: params.get('admin_consent'),
        tenantId: params.get('tenant') || params.get('state'),
        error: params.get('error'),
        errorDescription: params.get('error_description')
      };
    } catch (e) {
      console.error("Error parsing URL:", e);
      return { 
        adminConsent: null, 
        tenantId: null, 
        error: "invalid_url", 
        errorDescription: "Could not parse URL" 
      };
    }
  };

  const handleCancelOnboarding = () => {
    // Show confirmation dialog
    if (window.confirm("Are you sure you want to exit the onboarding process? You'll need to sign in again.")) {
      // Clear any intervals
      if (consentCheckInterval) {
        clearInterval(consentCheckInterval);
        setConsentCheckInterval(null);
      }
      
      // Execute the onCancel callback (defaults to logout)
      onCancel();
    }
  };

  // Step 1: Check if tenant is already onboarded and generate consent URL
  useEffect(() => {
    if (tenantId) {
      if (tenantVerified) {
        console.log("Tenant already verified by parent component, skipping onboarding flow");
        onOnboardingComplete();
        return;
      }
      
      // First check if clientId exists before using it
      if (!clientId) {
        console.error("Missing client ID in TenantManager component");
        setError("Configuration error: Missing client ID");
        return;
      }
      
      // Safe to use clientId after validation
      console.log(`Using client ID: ${clientId.substring(0, 4)}...${clientId.substring(clientId.length - 4)}`);    
        
      // Generate admin consent URL - use the same origin for the redirect
      const redirectUri = `${window.location.origin}/admin-consent-callback`;
      console.log("Generated redirect URI:", redirectUri);
      
      const adminConsentUrl = `https://login.microsoftonline.com/${tenantId}/adminconsent` +
        `?client_id=${clientId}` +
        `&redirect_uri=${encodeURIComponent(redirectUri)}` +
        `&state=${tenantId}`;
        
      setAdminConsentUrl(adminConsentUrl);
    }
  }, [tenantId, onOnboardingComplete, tenantVerified, clientId]);

  // Step 2: Request admin consent with enhanced popup handling
  const requestAdminConsent = () => {
    if (!adminConsentUrl) {
      setError("Admin consent URL not generated yet");
      return;
    }
    
    setLoading(true);
    setError(null);
    setConsentVerificationState('waiting'); // Set initial state
    
    // Clear any existing interval
    if (consentCheckInterval) {
      clearInterval(consentCheckInterval);
      setConsentCheckInterval(null);
    }
    
    console.log("Opening admin consent URL:", adminConsentUrl);
    
    // Open admin consent URL in a new window
    const consentWindow = window.open(adminConsentUrl, '_blank', 'width=800,height=600');
    
    if (!consentWindow) {
      setLoading(false);
      setError("Popup blocked. Please allow popups and try again.");
      setConsentVerificationState('idle');
      return;
    }
    
    let consentCheckAttempts = 0;
    const maxAttempts = 10; // Allow up to 10 polling attempts
    
    // Set up polling to check if consent was granted
    const interval = window.setInterval(() => {
      // First, check if the window is closed
      if (consentWindow.closed) {
        clearInterval(interval);
        setConsentCheckInterval(null);
        
        // Allow 5 seconds for consent to propagate before checking
        console.log("Consent window closed, waiting for permissions to propagate...");
        setDebugInfo(prev => ({ ...prev, consentWindowClosed: true, closedAt: new Date().toISOString() }));
        
        // Show an intermediate state to the user
        setLoading(true);
        setConsentVerificationState('checking');
        
        setTimeout(() => {
          verifyConsent().catch(err => {
            console.error("Error verifying consent after window closed:", err);
            setError("Failed to verify permissions. Please try again.");
            setLoading(false);
            setConsentVerificationState('failed');
          });
        }, 8000);
        
        return;
      }
      
      // If window is still open, increment attempt counter
      consentCheckAttempts++;
      
      // Try to access the window location to check for completion
      try {
        // If we can access the URL and it contains our callback URL, it means
        // the auth completed but the window didn't close automatically
        const currentUrl = consentWindow.location.href;
        
        if (currentUrl.includes('/admin-consent-callback')) {
          console.log("Detected callback URL in consent window:", currentUrl);
          
          // Extract parameters from URL
          const params = extractParamsFromUrl(currentUrl);
          const adminConsent = params.adminConsent;
          const error = params.error;
          
          // Close the window since we've detected the callback
          consentWindow.close();
          
          clearInterval(interval);
          setConsentCheckInterval(null);
          
          if (error) {
            console.error("Error in admin consent response:", error);
            setError(`Consent failed: ${params.errorDescription || error}`);
            setLoading(false);
            setConsentVerificationState('failed');
          } else if (adminConsent === 'True') {
            console.log("Admin consent successful according to URL parameters");
            
            // Verify consent to confirm
            setConsentVerificationState('checking');
            setTimeout(() => {
              verifyConsent().catch(err => {
                console.error("Error verifying consent after successful response:", err);
                setError("Failed to verify permissions. Please try again.");
                setLoading(false);
                setConsentVerificationState('failed');
              });
            }, 5000);
          }
        }
      } catch (e) {
        // This error is expected when trying to access cross-origin content
        // It means the window is on the Microsoft domain, which is normal during consent
        if (consentCheckAttempts >= maxAttempts) {
          console.log("Max polling attempts reached, waiting for window close event");
        }
      }
    }, 1000);
    
    setConsentCheckInterval(interval as unknown as number);
  };

  // Step 3: Verify consent was granted with enhanced error handling
  const verifyConsent = async (retryCount = 0, maxRetries = 3) => {
    if (!tenantId) return;
    
    setLoading(true);
    setError(null);
    setConsentVerificationState('checking');
    
    try {
      console.log(`Verifying admin consent for tenant: ${tenantId} (attempt ${retryCount + 1}/${maxRetries + 1})`);
      
      // First try with a lightweight scope to check authentication
      const userInfoToken = await instance.acquireTokenSilent({
        scopes: ["User.Read"],
        account: currentAccount as AccountInfo
      });
      
      console.log("Successfully acquired User.Read token");
      
      // Add a base delay that increases with retry attempts
      const baseDelay = 3000; // 3 seconds base delay
      const retryDelay = baseDelay + (retryCount * 2000); // +2 seconds per retry
      
      if (retryCount > 0) {
        console.log(`Waiting ${retryDelay}ms before retry attempt...`);
        await new Promise(resolve => setTimeout(resolve, retryDelay));
      } else {
        // Even on first attempt, wait a bit for permissions to propagate
        console.log(`Waiting ${baseDelay}ms for permissions to propagate...`);
        await new Promise(resolve => setTimeout(resolve, baseDelay));
      }
      
      // Create a timeout promise
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error("Token acquisition timed out")), 15000); // Increased timeout
      });
      
      // Now try the high-privilege scope with timeout
      const tokenPromise = instance.acquireTokenSilent({
        scopes: ["https://graph.microsoft.com/Application.ReadWrite.All"],
        account: currentAccount as AccountInfo
      });
      
      // Race the token acquisition against the timeout
      const highPrivilegeToken = await Promise.race([tokenPromise, timeoutPromise]);
      
      console.log("Successfully acquired Application.ReadWrite.All token");
      
      // Make an actual API call to validate permissions
      const response = await fetch('https://graph.microsoft.com/v1.0/applications?$top=1', {
        headers: {
          'Authorization': `Bearer ${highPrivilegeToken.accessToken}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        console.log("Graph API call successful - permission verification complete");
        
        // Register tenant as onboarded
        const onboardedTenants = JSON.parse(localStorage.getItem('onboardedTenants') || '[]');
        if (!onboardedTenants.includes(tenantId)) {
          onboardedTenants.push(tenantId);
          localStorage.setItem('onboardedTenants', JSON.stringify(onboardedTenants));
          console.log("Tenant added to onboarded list:", tenantId);
        }
        
        // Add a marker that consent was just completed
        sessionStorage.setItem(`consent_completed_${tenantId}`, Date.now().toString());
        
        // Move to the next step - success!
        setActiveStep(1);
        setLoading(false);
        setConsentVerificationState('success');
        
        // Gather debug info about the token
        setDebugInfo({
          tokenAcquired: true,
          scopes: ["https://graph.microsoft.com/Application.ReadWrite.All"],
          timestamp: new Date().toISOString(),
          tokenExpiration: highPrivilegeToken.expiresOn ? 
            new Date(highPrivilegeToken.expiresOn).toISOString() : 
            new Date().toISOString(),
          retryAttempt: retryCount + 1
        });
      } else {
        const errorText = await response.text();
        console.error("API call failed despite getting token:", errorText);
        
        // Retry logic for API failures
        if (retryCount < maxRetries) {
          console.log(`API call failed, retrying in ${(retryCount + 1) * 2000}ms...`);
          setTimeout(() => {
            verifyConsent(retryCount + 1, maxRetries);
          }, (retryCount + 1) * 2000);
          return;
        }
        
        setLoading(false);
        setError("Admin consent was granted but API access failed after multiple attempts. Please try the consent process again.");
        setConsentVerificationState('failed');
        setDebugInfo((prev: DebugInfo) => ({ 
          ...prev, 
          apiCallFailed: true, 
          apiErrorText: errorText,
          timestamp: new Date().toISOString(),
          finalRetryAttempt: retryCount + 1
        }));
      }
    } catch (error) {
      console.error("Admin consent verification failed:", error);
      
      // Retry logic for token acquisition failures
      if (error instanceof InteractionRequiredAuthError && retryCount < maxRetries) {
        console.log(`Token acquisition failed, retrying in ${(retryCount + 1) * 3000}ms...`);
        setTimeout(() => {
          verifyConsent(retryCount + 1, maxRetries);
        }, (retryCount + 1) * 3000);
        return;
      }
      
      setLoading(false);
      setConsentVerificationState('failed');
      
      // Check if it's a consent required error
      if (error instanceof InteractionRequiredAuthError) {
        if (retryCount >= maxRetries) {
          setError("Admin consent verification failed after multiple attempts. Please try the consent process again.");
        } else {
          setError("Admin consent was not properly granted. Please try again.");
        }
        setDebugInfo({
          tokenAcquired: false,
          error: error.message,
          errorCode: error.errorCode,
          timestamp: new Date().toISOString(),
          retryAttempt: retryCount + 1
        });
      } else {
        setError("An unexpected error occurred. Please try again.");
        setDebugInfo({
          tokenAcquired: false,
          error: error instanceof Error ? error.message : String(error),
          timestamp: new Date().toISOString(),
          retryAttempt: retryCount + 1
        });
      }
    }
  };  

  // Helper to acquire token with scopes
  const acquireTokenWithScopes = async (scopes: string[]): Promise<string> => {
    try {
      const response = await instance.acquireTokenSilent({
        scopes: scopes,
        account: currentAccount as AccountInfo
      });
      
      return response.accessToken;
    } catch (error) {
      // If silent acquisition fails, try with popup
      if (error instanceof InteractionRequiredAuthError) {
        const response = await instance.acquireTokenPopup({
          scopes: scopes,
          account: currentAccount as AccountInfo
        });
        
        return response.accessToken;
      }
      
      throw error;
    }
  };

  // Step 4: Complete onboarding
  const completeOnboarding = () => {
    if (tenantId) {
      // Save any additional tenant information if needed
      const tenantUserMap = JSON.parse(localStorage.getItem('tenantUserMap') || '{}');
      tenantUserMap[tenantId] = userEmail;
      localStorage.setItem('tenantUserMap', JSON.stringify(tenantUserMap));
      
      // Complete onboarding
      onOnboardingComplete();
    }
  };

  // Handle retry
  const handleRetry = () => {
    setActiveStep(0);
    setError(null);
    setLoading(false);
    setConsentVerificationState('idle');
  };

  // Steps for the stepper
  const steps = [
    'Request Administrator Consent',
    'Confirm Access',
    'Start Using the App'
  ];

  return (
    <>
      {/* Welcome Dialog for First-Time Users */}
      <Dialog 
        open={showWelcomeDialog} 
        onClose={() => setShowWelcomeDialog(false)}
        maxWidth="md"
      >
        <DialogTitle sx={{ 
          bgcolor: '#f8f9fa', 
          borderBottom: '1px solid #e0e0e0',
          display: 'flex',
          alignItems: 'center'
        }}>
          <AppSettingsAltIcon sx={{ mr: 1, color: '#ff6b00' }} />
          <Typography variant="h5" component="div">
            Welcome to AppConfig²
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 1 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Hello, {userDisplayName}!
            </Typography>
            <Typography variant="body1" paragraph>
              You're just a few steps away from using our powerful app configuration management tool for Microsoft Entra ID (formerly Azure AD).
            </Typography>
          </Box>

          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                    <AdminPanelSettingsIcon sx={{ mr: 1, color: '#ff6b00' }} />
                    What is AppConfig²?
                  </Typography>
                  <Typography variant="body2" paragraph>
                    AppConfig² is a specialized tool that helps you manage and configure 
                    your applications in Microsoft Entra ID. It simplifies complex tasks like:
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <CheckCircleIcon fontSize="small" color="primary" />
                      </ListItemIcon>
                      <ListItemText primary="Testing authentication flows (SAML & OAuth)" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <CheckCircleIcon fontSize="small" color="primary" />
                      </ListItemIcon>
                      <ListItemText primary="Managing application manifests easily" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <CheckCircleIcon fontSize="small" color="primary" />
                      </ListItemIcon>
                      <ListItemText primary="Configuring claims mapping policies" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <CheckCircleIcon fontSize="small" color="primary" />
                      </ListItemIcon>
                      <ListItemText primary="Analyzing app permissions and security" />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                    <SecurityIcon sx={{ mr: 1, color: '#ff6b00' }} />
                    Why Administrator Consent?
                  </Typography>
                  <Typography variant="body2" paragraph>
                    To provide you with powerful management capabilities, AppConfig² needs permission to:
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <VerifiedUserIcon fontSize="small" color="primary" />
                      </ListItemIcon>
                      <ListItemText primary="Access your organization's directory" secondary="To list and discover applications" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <VerifiedUserIcon fontSize="small" color="primary" />
                      </ListItemIcon>
                      <ListItemText primary="Manage application registrations" secondary="To update configurations and settings" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <VerifiedUserIcon fontSize="small" color="primary" />
                      </ListItemIcon>
                      <ListItemText primary="Read and write policies" secondary="For claims mapping and other policy configurations" />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Typography variant="body2" color="text.secondary" paragraph>
            In the next steps, you'll be prompted to grant administrator consent for these permissions. 
            Only a Global Administrator can complete this step.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #e0e0e0' }}>
          <Button 
            onClick={handleCancelOnboarding} 
            variant="outlined" 
            color="inherit"
            startIcon={<ArrowBackIcon />}
          >
            Back to Sign In
          </Button>  

          <Button onClick={() => setShowWelcomeDialog(false)} variant="contained">
            Continue to Onboarding
          </Button>
        </DialogActions>
      </Dialog>

      {/* Permissions Info Dialog */}
      <Dialog 
        open={showPermissionsInfo} 
        onClose={() => setShowPermissionsInfo(false)}
        maxWidth="md"
      >
        <DialogTitle sx={{ bgcolor: '#f8f9fa', borderBottom: '1px solid #e0e0e0' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <LockOpenIcon sx={{ mr: 1, color: '#ff6b00' }} />
            <Typography variant="h6">
              Understanding Required Permissions
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph sx={{ mt: 2 }}>
            AppConfig² requests the following permissions to function properly:
          </Typography>
          
          <List>
            {allRequiredScopes.map((scope) => (
              <ListItem key={scope} divider>
                <ListItemIcon>
                  <VerifiedUserIcon color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary={scope} 
                  secondary={permissionDescriptions[scope]} 
                />
              </ListItem>
            ))}
          </List>
          
          <Box sx={{ mt: 3 }}>
            <Typography variant="body2" color="text.secondary">
              These permissions are used only for the functionality within AppConfig² and are not shared with third parties.
              For more information, please review our <Link href="#" underline="hover">privacy policy</Link>.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPermissionsInfo(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Main Onboarding Process */}
      <Box sx={{ maxWidth: 700, mx: 'auto', mt: 4, p: 3 }}>
        <Paper sx={{ p: 4, borderRadius: 2, boxShadow: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h5" sx={{ color: '#333', fontWeight: 600 }}>
              Tenant Onboarding
            </Typography>
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              onClick={handleCancelOnboarding}
              startIcon={<CloseIcon />}
              sx={{ borderColor: '#ddd' }}
            >
              Exit
            </Button>
          </Box>
          
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
          
          {activeStep === 0 && (
            <Box>
              <Alert severity="info" sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
                <InfoIcon sx={{ mr: 1 }} />
                <Box>
                  <AlertTitle>Administrator Access Required</AlertTitle>
                  <Typography variant="body2">
                    This step requires a <strong>Global Administrator</strong> role.
                  </Typography>
                </Box>
              </Alert>
              
              <Typography variant="body1" paragraph>
                AppConfig² requires administrator consent to access your organization's data and manage applications. 
                Please click the button below to start the consent process.
              </Typography>
              
              <Typography variant="body2" color="text.secondary" paragraph>
                When prompted, sign in with an administrator account for your organization
                and approve the requested permissions.
              </Typography>
              
              {loading && consentVerificationState === 'waiting' && (
                <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
                  <AlertTitle>Waiting for Admin Consent</AlertTitle>
                  <Typography variant="body2">
                    Please complete the consent process in the popup window.
                  </Typography>
                </Alert>
              )}

              {loading && consentVerificationState === 'checking' && (
                <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
                  <AlertTitle>Verifying Permissions</AlertTitle>
                  <Typography variant="body2">
                    Admin consent received. Verifying permissions...
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    <Typography variant="caption">This may take a few moments</Typography>
                  </Box>
                </Alert>
              )}
              
              <Box sx={{ mt: 3, mb: 3 }}>
                <Accordion elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <HelpOutlineIcon sx={{ mr: 1, color: '#ff6b00' }} />
                      <Typography>What happens during admin consent?</Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body2" paragraph>
                      During the admin consent process:
                    </Typography>
                    <ol>
                      <li>A new window will open to Microsoft's authentication page</li>
                      <li>You'll sign in with administrator credentials</li>
                      <li>You'll review and approve the permissions AppConfig² needs</li>
                      <li>After approval, you'll be redirected back to complete the setup</li>
                    </ol>
                    <Button 
                      size="small" 
                      variant="outlined" 
                      onClick={() => setShowPermissionsInfo(true)}
                      startIcon={<LockOpenIcon />}
                      sx={{ mt: 1 }}
                    >
                      View Detailed Permissions
                    </Button>
                  </AccordionDetails>
                </Accordion>
              </Box>
              
              {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  <AlertTitle>Error</AlertTitle>
                  {error}
                  {consentVerificationState === 'failed' && (
                    <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      <Button 
                        size="small" 
                        variant="outlined" 
                        onClick={handleRetry} 
                        startIcon={<ErrorIcon />}
                        color="error"
                      >
                        Try Again
                      </Button>
                      <Button 
                        size="small" 
                        variant="outlined" 
                        color="inherit"
                        href="/manual-onboarding.html"
                        target="_blank"
                        startIcon={<HelpOutlineIcon />}
                        sx={{ borderColor: '#ddd' }}
                      >
                        Manual Instructions
                      </Button>
                    </Box>
                  )}
                </Alert>
              )}
              
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Button 
                  variant="contained" 
                  onClick={requestAdminConsent}
                  disabled={loading}
                  sx={{ 
                    minWidth: 220,
                    py: 1.5,
                    fontSize: '1rem',
                    backgroundColor: '#ff6b00',
                    '&:hover': {
                      backgroundColor: '#e65c00',
                    }
                  }}
                  startIcon={loading ? undefined : <AdminPanelSettingsIcon />}
                >
                  {loading ? (
                    <>
                      <CircularProgress size={24} sx={{ mr: 1 }} />
                      {consentVerificationState === 'waiting' ? 'Waiting for Consent...' : 
                       consentVerificationState === 'checking' ? 'Verifying Permissions...' : 
                       'Processing...'}
                    </>
                  ) : 'Request Admin Consent'}
                </Button>
              </Box>
              
              {/* Developer tools - hidden by default */}
              <Box sx={{ mt: 4, pt: 2, borderTop: '1px solid #eee' }}>
                <Button 
                  size="small" 
                  onClick={() => setShowDebugInfo(!showDebugInfo)}
                  variant="text"
                  color="inherit"
                >
                  {showDebugInfo ? 'Hide' : 'Show'} Debug Info
                </Button>
                
                {showDebugInfo && (
                  <Box sx={{ mt: 1, p: 2, bgcolor: '#f5f5f5', borderRadius: 1, fontSize: '0.75rem' }}>
                    <Typography variant="caption" component="div">
                      <pre>{JSON.stringify({...debugInfo, consentVerificationState}, null, 2)}</pre>
                    </Typography>
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        Developer Tools - For troubleshooting only:
                      </Typography>
                      <Button 
                        size="small" 
                        onClick={() => verifyConsent()}
                        variant="outlined"
                        color="warning"
                        startIcon={<BuildIcon />}
                        sx={{ 
                          fontSize: '0.7rem',
                          borderColor: 'rgba(237, 108, 2, 0.5)'
                        }}
                      >
                        Verify Consent Without Browser Flow
                      </Button>
                    </Box>
                  </Box>
                )}

                {activeStep === 0 && consentVerificationState !== 'success' && (
                  <Box sx={{ borderTop: '1px solid #eee', pt: 2, mt: 2, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      Having trouble with the automated onboarding process?
                    </Typography>
                    <Link 
                      href="/manual-onboarding.html" 
                      target="_blank"
                      underline="hover"
                      sx={{ fontSize: '0.875rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}
                    >
                      View manual onboarding instructions
                      <OpenInNewIcon sx={{ fontSize: '0.875rem' }} />
                    </Link>
                  </Box>
                )}
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                <Button
                  variant="text"
                  color="inherit"
                  onClick={handleCancelOnboarding}
                  startIcon={<ArrowBackIcon />}
                >
                  Back to Sign In
                </Button>            
              </Box>
            </Box>
          )}
          
          {activeStep === 1 && (
            <Box>
              <Alert severity="success" sx={{ mb: 3 }}>
                <AlertTitle>Admin Consent Granted</AlertTitle>
                <Typography variant="body2">
                  Administrator consent has been successfully granted for your tenant.
                </Typography>
              </Alert>
              
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                <CheckCircleIcon sx={{ fontSize: 60, color: '#4caf50' }} />
              </Box>
              
              <Typography variant="body1" paragraph align="center">
                Congratulations! Your organization has been successfully onboarded to AppConfig².
              </Typography>
              
              <Typography variant="body2" paragraph align="center" color="text.secondary">
                You now have access to all features and tools to manage your Entra ID applications.
              </Typography>
              
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <Button 
                  variant="contained" 
                  onClick={completeOnboarding}
                  sx={{ 
                    minWidth: 220,
                    py: 1.5,
                    fontSize: '1rem',
                    backgroundColor: '#ff6b00',
                    '&:hover': {
                      backgroundColor: '#e65c00',
                    }
                  }}
                >
                  Continue to App
                </Button>
              </Box>
            </Box>
          )}
        </Paper>
      </Box>
    </>
  );
};

export default TenantManager;